import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot } from '@angular/router';
import { Observable, of, from } from 'rxjs';
import { catchError, map, switchMap, take } from 'rxjs/operators';
import { CalAngularService } from '@cvx/cal-angular';
import { AuthService } from '#services/shared';
import { Role } from 'enums';

/**
 * Custom Role Guard that works with the app's internal role system.
 * Waits for CAL authentication to complete, then checks roles from backend.
 */
@Injectable({
  providedIn: 'root'
})
export class AppRoleGuard implements CanActivate {
  constructor(
    private calService: CalAngularService,
    private authService: AuthService,
    private router: Router
  ) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean> {
    const requiredRoles = route.data['roles'] as Role[];

    // If no roles required, just allow
    if (!requiredRoles || requiredRoles.length === 0) {
      return of(true);
    }

    // Wait for user to be signed in before checking roles
    return this.calService.isUserSignedIn().pipe(
      take(1),
      switchMap((isSignedIn) => {
        if (!isSignedIn) {
          // User not signed in yet, let CalGuardService handle it
          // Return true to allow the guard chain to continue
          console.log('AppRoleGuard: User not signed in, deferring to CalGuardService');
          return of(true);
        }
        
        // User is signed in, check roles
        return this.checkUserRole(requiredRoles);
      }),
      catchError((error) => {
        console.error('AppRoleGuard error:', error);
        return of(true); // Allow access on error, let API handle auth
      })
    );
  }

  private checkUserRole(requiredRoles: Role[]): Observable<boolean> {
    // Try to get role from localStorage first
    const roleId = localStorage.getItem('currentUserRole');
    
    if (roleId) {
      const userRole = Number(roleId) as Role;
      const hasAccess = requiredRoles.includes(userRole);
      
      if (!hasAccess) {
        this.router.navigate(['/error/not-authorized']);
      }
      
      return of(hasAccess);
    }

    // If no role in localStorage, load user info from backend
    return from(this.authService.loadUserInfo()).pipe(
      map((user) => {
        if (user && user.RoleID) {
          const hasAccess = requiredRoles.includes(user.RoleID);
          if (!hasAccess) {
            this.router.navigate(['/error/not-authorized']);
          }
          return hasAccess;
        }
        this.router.navigate(['/error/not-authorized']);
        return false;
      }),
      catchError((error) => {
        console.error('Failed to load user info:', error);
        // On 401, user might not be fully authenticated yet
        // Allow navigation and let the page handle it
        return of(true);
      })
    );
  }
}
